import React from 'react'

import type {
  LotStoryGroupDeliveryView,
  TranslateFn
} from './lotStoryLibraryTypes'

type LotStoryGroupDeliveryCardProps = {
  t: TranslateFn
  delivery: LotStoryGroupDeliveryView
  testIdPrefix: string
  renderedProjection?: React.ReactNode
  renderedReceipt?: React.ReactNode
}

const getTestId = (prefix: string, suffix?: string) => suffix ? `${prefix}-${suffix}` : prefix

export default function LotStoryGroupDeliveryCard({
  t,
  delivery,
  testIdPrefix,
  renderedProjection,
  renderedReceipt
}: Readonly<LotStoryGroupDeliveryCardProps>) {
  const isLoading = delivery.status === 'loading' || delivery.status === 'idle'
  const statusMessage = delivery.status === 'allowed'
    ? t('lots.story.groupDelivery.status.allowed')
    : t('lots.story.groupDelivery.status.denied')

  return (
    <div
      data-testid={getTestId(testIdPrefix)}
      style={{
        border: '1px solid #bbf7d0',
        borderRadius: 10,
        backgroundColor: '#f0fdf4',
        padding: 12,
        display: 'grid',
        gap: 8
      }}
    >
      <div style={{ display: 'grid', gap: 4 }}>
        <strong style={{ fontSize: 12, color: '#166534' }}>{t('lots.story.groupDelivery.title')}</strong>
        <div style={{ fontSize: 13, color: '#166534' }}>{t('lots.story.groupDelivery.subtitle')}</div>
      </div>

      {isLoading ? (
        <div data-testid={getTestId(testIdPrefix, 'status')} style={{ fontSize: 13, color: '#166534', fontWeight: 700 }}>
          {t('lots.story.groupDelivery.loading')}
        </div>
      ) : delivery.errorMessage ? (
        <div data-testid={getTestId(testIdPrefix, 'status')} style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>
          {delivery.errorMessage}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6, fontSize: 13, color: '#166534' }}>
          <div data-testid={getTestId(testIdPrefix, 'selected-group')}>
            <strong>{t('lots.story.groupDelivery.selectedGroup')}:</strong> {delivery.selectedGroupLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'saved-group')}>
            <strong>{t('lots.story.groupDelivery.savedGroup')}:</strong> {delivery.savedGroupLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'saved-match')}>
            <strong>{t('lots.story.groupDelivery.savedMatch')}:</strong> {delivery.selectedGroupMatchesSavedIntent
              ? t('lots.story.groupShareReview.savedMatch.yes')
              : t('lots.story.groupShareReview.savedMatch.no')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'identity')}>
            <strong>{t('lots.story.groupDelivery.identity')}:</strong> {delivery.identityLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'role')}>
            <strong>{t('lots.story.groupDelivery.tenantRole')}:</strong> {delivery.roleLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'granted-group')}>
            <strong>{t('lots.story.groupDelivery.grantedGroup')}:</strong> {delivery.grantedGroupLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'granted-role')}>
            <strong>{t('lots.story.groupDelivery.grantedGroupRole')}:</strong> {delivery.grantedGroupRoleLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'partners')}>
            <strong>{t('lots.story.groupDelivery.partnerOrganizations')}:</strong> {delivery.partnerOrganizationsLabel || t('lots.story.groupPreflight.none')}
          </div>
          <div data-testid={getTestId(testIdPrefix, 'reasons')}>
            <strong>{t('lots.story.groupDelivery.blockingReasons')}:</strong> {delivery.blockingReasonLabels.length > 0
              ? delivery.blockingReasonLabels.join('; ')
              : t('lots.story.groupPreflight.none')}
          </div>
          <div
            data-testid={getTestId(testIdPrefix, 'status')}
            style={{
              color: delivery.status === 'allowed' ? '#166534' : '#b91c1c',
              fontWeight: 700
            }}
          >
            {statusMessage}
          </div>

          {delivery.status === 'allowed' ? (
            <>
              <div data-testid={getTestId(testIdPrefix, 'title')}>
                <strong>{t('lots.story.groupDelivery.snapshotTitle')}:</strong> {delivery.deliveryTitle || t('lots.story.groupPreflight.none')}
              </div>
              <div data-testid={getTestId(testIdPrefix, 'published-at')}>
                <strong>{t('lots.story.groupDelivery.publishedAt')}:</strong> {delivery.publishedAtLabel || t('lots.story.groupPreflight.none')}
              </div>
              <div data-testid={getTestId(testIdPrefix, 'published-by')}>
                <strong>{t('lots.story.groupDelivery.publishedBy')}:</strong> {delivery.publishedByLabel || t('lots.story.groupPreflight.none')}
              </div>
              {delivery.deliverySummary ? (
                <div data-testid={getTestId(testIdPrefix, 'summary')}>
                  <strong>{t('lots.story.groupDelivery.summary')}:</strong> {delivery.deliverySummary}
                </div>
              ) : null}
              {delivery.deliveryNotes ? (
                <div data-testid={getTestId(testIdPrefix, 'notes')} style={{ whiteSpace: 'pre-wrap' }}>
                  <strong>{t('lots.story.groupDelivery.notes')}:</strong> {delivery.deliveryNotes}
                </div>
              ) : null}
              {renderedProjection ? (
                <div data-testid={getTestId(testIdPrefix, 'projection')} style={{ display: 'grid', gap: 10 }}>
                  {renderedProjection}
                </div>
              ) : null}
              {renderedReceipt ? (
                <div data-testid={getTestId(testIdPrefix, 'receipt')} style={{ display: 'grid', gap: 10 }}>
                  {renderedReceipt}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}